import React from 'react';
import PropTypes from 'prop-types';
import { ApolloProvider } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import { Provider } from 'react-redux';
import { SnackbarProvider } from 'notistack';
import { IntlProvider } from 'react-intl';
import gql from 'graphql-tag';
import client from '../../lib/apolloClient';
import configureStore from '../../store/findBobPublicStore';
import getMessages from '../../lib/i18n/getMessages';
import { CurrencyProvider } from '../../hooks/useCurrency';
import SuccessionThemeProvider from '../../components/Succession/SuccessionThemeProvider';
import SuccessionProfileEditPage from '../../components/Succession/ProfileEdit/SuccessionProfileEditPage';

// GraphQL Query for current user profile
const GET_CURRENT_USER_PROFILE = gql`
  query GetCurrentUserProfile {
    getCurrentUser {
      id
      name
      email
      headline
      bio
      location
      profile_picture
      profile_percentage_complete
      # Canonical per-section completion, computed server-side by
      # Profile::SectionCompletion. The progress banner must render this and never
      # re-derive completion from raw fields — a second ruleset is the bug these
      # fields exist to remove.
      profile_section_percentage
      profile_section_completion {
        key
        complete
        countable
        reason_not_countable
      }
      ask_me_about
      motivation
      pending_motivation
      can_edit_motivation
      show_real_name
      transition_goals
      planning_preference
      primary_markets
      specialized_businesses
      practice_areas
      why_values
      interests
      sports
      languages
      years_practicing
      avg_client_age
      annual_revenue
      assets_under_management
      number_of_clients
      fee_based_revenue
      trailer_revenue
      direct_revenue
      transactional_revenue
      recurring_revenue
      gross_production
      year_1_gross
      year_2_gross
      year_3_gross
      year_4_gross
      year_5_gross
      designations
      advisor_licenses
      business_continuity_state
      business_continuity_state_id
      succession_planning_state_id
      target_timeframe
      workweek_trajectory_now
      workweek_trajectory_year_3
      workweek_trajectory_year_5
      workweek_trajectory_year_10
      workweek_trajectory_year_15
      workweek_trajectory_year_20
      workweek_trajectory_year_25
      bankruptcy_in_last_x_years
      charged_with_felony
      credit_score_above_x
      tax_delinquency
      defined_investment_style
      defined_service_model
      part_of_team
      team_size
      number_of_employees
      niche_market
      good_standing_with_regulator
      persistency
      percent_revenue_recurring
      percent_non_recurring_revenue
      percent_fee_based
      percent_commission
      # Life Insurance fields
      li_first_year_commissions
      li_renewal_commissions
      li_override_commissions
      li_other_revenues
      li_policy_count
      life_year_1_gross
      life_year_2_gross
      life_year_3_gross
      life_year_4_gross
      life_year_5_gross
      # P&C Insurance fields
      pc_personal_lines_premium
      pc_personal_lines_commission_rate
      pc_commercial_lines_premium
      pc_commercial_lines_commission_rate
      pc_personal_retention_rate
      pc_commercial_retention_rate
      pc_year_1_commission
      pc_year_2_commission
      pc_year_3_commission
      pc_year_4_commission
      pc_year_5_commission
      # Group Insurance fields
      group_first_year_commissions
      group_renewal_commissions
      group_override_commissions
      group_other_revenues
      group_number_of_cases
      group_average_case_size
      group_case_retention_rate
      group_year_1_gross
      group_year_2_gross
      group_year_3_gross
      group_year_4_gross
      group_year_5_gross
      recognition_level
      update_production_data_automatically
      company {
        id
        name
        feature_types
        avatar_directory
        avatar_count
        avatar_background_color
        primary_color
        new_avatars_enabled
        imported_production_data_read_only
        revenue_line_terminology
      }
    }
  }
`;

// GraphQL Query for available options
const GET_AVAILABLE_OPTIONS = gql`
  query GetAvailableOptions {
    getSuccessionTransitionGoals
    getGrowthTransitionGoals
    availablePrimaryMarkets
    availableSpecializedBusinesses
    availablePracticeAreas
    availableWhyValues
    availableLanguages {
      id
      name
    }
    availableInterests {
      id
      name
    }
    availableSports {
      id
      name
    }
    availableDesignations {
      id
      name
    }
    availableAdvisorLicenses
    availableAvatars
  }
`;

const SuccessionProfileEditContainer = (props, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  window.currency = (railsContext && railsContext.currency) || window.currency || 'USD';
  // Initialize Redux store (use existing or create new)
  let store = {};
  if (document.store) {
    store = document.store;
  } else {
    store = configureStore(props);
    document.store = store;
  }

  const locale = window.locale;

  return (
    <CurrencyProvider currency={(railsContext && railsContext.currency) || window.currency || 'USD'}>
    <IntlProvider locale={locale} messages={getMessages(locale)}>
    <Provider store={store}>
      <ApolloProvider client={client}>
        <SuccessionThemeProvider>
          <SnackbarProvider maxSnack={3}>
            <Query query={GET_CURRENT_USER_PROFILE} client={client}>
              {({ loading: userLoading, error: userError, data: userData, refetch }) => {
                if (userLoading) return <div className="flex items-center justify-center min-h-screen">{locale === 'fr' ? 'Chargement du profil...' : 'Loading profile...'}</div>;
                if (userError) return <div className="flex items-center justify-center min-h-screen text-red-600">Error loading profile: {userError.message}</div>;

                return (
                  <Query query={GET_AVAILABLE_OPTIONS} client={client}>
                    {({ loading: optionsLoading, error: optionsError, data: optionsData }) => {
                      if (optionsLoading) return <div className="flex items-center justify-center min-h-screen">{locale === 'fr' ? 'Chargement des options...' : 'Loading options...'}</div>;
                      if (optionsError) return <div className="flex items-center justify-center min-h-screen text-red-600">Error loading options: {optionsError.message}</div>;

                      // Debug logging for avatars
                      console.log('🔍 SuccessionProfileEditContainer - Available avatars from backend:', optionsData.availableAvatars);

                      // Build company settings for avatar display
                      const company = userData.getCurrentUser?.company || {};
                      const companySettings = {
                        new_avatars_enabled: company.new_avatars_enabled,
                        avatar_directory: company.avatar_directory,
                        avatar_count: company.avatar_count,
                        avatar_background_color: company.avatar_background_color,
                        primary_color: company.primary_color,
                      };

                      return (
                        <SuccessionProfileEditPage
                          user={userData.getCurrentUser}
                          companySettings={companySettings}
                          options={{
                            successionTransitionGoals: optionsData.getSuccessionTransitionGoals || [],
                            growthTransitionGoals: optionsData.getGrowthTransitionGoals || [],
                            primaryMarkets: optionsData.availablePrimaryMarkets || [],
                            specializedBusinesses: optionsData.availableSpecializedBusinesses || [],
                            practiceAreas: optionsData.availablePracticeAreas || [],
                            whyValues: optionsData.availableWhyValues || [],
                            languages: optionsData.availableLanguages || [],
                            interests: optionsData.availableInterests || [],
                            sports: optionsData.availableSports || [],
                            designations: optionsData.availableDesignations || [],
                            advisorLicenses: optionsData.availableAdvisorLicenses || [],
                            avatars: optionsData.availableAvatars || [],
                          }}
                          refetchUser={refetch}
                          quickbooksConnected={props.quickbooks_connected}
                          quickbooksLastSyncedAt={props.quickbooks_last_synced_at}
                          quickbooksConnectUrl={props.quickbooks_connect_url}
                          quickbooksDisconnectUrl={props.quickbooks_disconnect_url}
                          quickbooksSyncUrl={props.quickbooks_sync_url}
                        />
                      );
                    }}
                  </Query>
                );
              }}
            </Query>
          </SnackbarProvider>
        </SuccessionThemeProvider>
      </ApolloProvider>
    </Provider>
    </IntlProvider>
    </CurrencyProvider>
  );
};

SuccessionProfileEditContainer.propTypes = {};

export default SuccessionProfileEditContainer;

